import {
  ActionList,
  Avatar,
  Button,
  ButtonGroup,
  Divider,
  Icon,
  Popover,
  Text,
  TopBar,
} from '@shopify/polaris';
import { ArrowLeftIcon, MobileIcon, PrintIcon, SoundIcon } from '@shopify/polaris-icons';
import { Box, Stack, Typography } from '@mui/material';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import authStore from 'src/stores/auth_store';
import soundStore from 'src/stores/sound_store';
import { getUserInitial, getUserName } from 'src/utils/format_data';
import { SOUND_OPTIONS, playSoundFile } from 'src/utils/sound';
import { useGetMe } from 'src/api/user_api';
import Loading from 'src/components/loading';
import AppInstallModal from 'src/components/dialog/app_install_modal';
import { CompactSwitch } from 'src/components/hook_form/rhf_switch';
import { ROLES } from 'src/layouts/guard/role_guard';
import { SCAN_SOUND_ROLES } from 'src/hooks/use_scan_sound';
import { useBoolean } from 'src/hooks/use_boolean';

const APP_INSTALL_HIDDEN_ROLES = [ROLES.CUSTOMER, ROLES.STAFF];
const QZ_TRAY_DOWNLOAD_URL = 'https://qz.io/download/';

const soundRowStyle = {
  minHeight: '36px',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--p-space-200)',
  paddingX: 'var(--p-space-150)',
};

export default function Topbar() {
  const { t } = useTranslation(['auth', 'app_release']);
  const { logout } = authStore();
  const { soundOn, successSound, failSound, setSoundOn, setSuccessSound, setFailSound } =
    soundStore();
  const { user, role, searchLoading } = useGetMe();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const appInstallOpen = useBoolean();

  const toggleIsUserMenuOpen = useCallback(
    () => setIsUserMenuOpen((isUserMenuOpen) => !isUserMenuOpen),
    [],
  );

  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), []);

  const handleNavigationToggle = useCallback(() => {}, []);

  const canInstallApp = Boolean(role) && !APP_INSTALL_HIDDEN_ROLES.includes(role);
  const canSetScanSound = SCAN_SOUND_ROLES.includes(role);

  const userName = getUserName(user);

  const topSections = [
    {
      items: [
        {
          content: t('app_release:install_qz_tray'),
          icon: PrintIcon,
          url: QZ_TRAY_DOWNLOAD_URL,
          external: true,
          onAction: () => setIsUserMenuOpen(false),
        },
      ],
    },
    ...(canInstallApp
      ? [
          {
            items: [
              {
                content: t('app_release:install_app'),
                icon: MobileIcon,
                onAction: () => {
                  setIsUserMenuOpen(false);
                  appInstallOpen.onTrue();
                },
              },
            ],
          },
        ]
      : []),
  ];

  const logoutSections = [
    {
      items: [
        {
          content: t('auth:logout'),
          icon: ArrowLeftIcon,
          onAction: async () => await logout(),
        },
      ],
    },
  ];

  const renderSoundPicker = (label, kind, selected, onSelect) => (
    <Box sx={{ ...soundRowStyle, paddingLeft: '34px' }}>
      <Typography
        variant="body_md"
        sx={{ flex: 1 }}
      >
        {label}
      </Typography>
      <ButtonGroup variant="segmented">
        {SOUND_OPTIONS.map((no) => (
          <Button
            key={no}
            size="slim"
            pressed={selected === no}
            accessibilityLabel={`${label} ${no}`}
            onClick={() => {
              onSelect(no);
              playSoundFile(`${kind}_${no}`);
            }}
          >
            {no}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );

  const soundSettingMarkup = (
    <Box sx={{ padding: 'var(--p-space-150)' }}>
      <Box sx={soundRowStyle}>
        <Icon source={SoundIcon} />
        <Typography
          variant="body_md"
          sx={{ flex: 1 }}
        >
          {t('app_release:scan_sound')}
        </Typography>
        <CompactSwitch
          size="small"
          disableRipple
          checked={soundOn}
          onChange={(event) => setSoundOn(event.target.checked)}
          inputProps={{ 'aria-label': t('app_release:scan_sound') }}
        />
      </Box>
      {soundOn && (
        <Stack>
          {renderSoundPicker(
            t('app_release:success_sound'),
            'success',
            successSound,
            setSuccessSound,
          )}
          {renderSoundPicker(t('app_release:fail_sound'), 'fail', failSound, setFailSound)}
        </Stack>
      )}
    </Box>
  );

  const userMenuActivator = (
    <div className="Polaris-TopBar-Menu__ActivatorWrapper">
      <button
        type="button"
        className="Polaris-TopBar-Menu__Activator Polaris-TopBar-Menu__Activator--userMenu"
        onClick={toggleIsUserMenuOpen}
      >
        <span className="Polaris-TopBar-UserMenu__Details">
          <Text
            as="p"
            variant="bodySm"
            alignment="start"
            fontWeight="medium"
            truncate
          >
            {userName}
          </Text>
        </span>
        <Avatar
          size="md"
          initials={getUserInitial(user)?.replace(' ', '')}
          name={userName}
        />
      </button>
    </div>
  );

  const userMenuMarkup = (
    <Popover
      active={isUserMenuOpen}
      activator={userMenuActivator}
      onClose={closeUserMenu}
      fixed
      fullHeight
      preferredAlignment="right"
    >
      <ActionList
        actionRole="menuitem"
        onActionAnyItem={closeUserMenu}
        sections={topSections}
      />
      <Divider />
      {canSetScanSound && (
        <>
          {soundSettingMarkup}
          <Divider />
        </>
      )}
      <ActionList
        actionRole="menuitem"
        onActionAnyItem={closeUserMenu}
        sections={logoutSections}
      />
    </Popover>
  );

  const topBarMarkup = (
    <>
      <TopBar
        showNavigationToggle
        userMenu={userMenuMarkup}
        onNavigationToggle={handleNavigationToggle}
      />
      <AppInstallModal
        isOpen={appInstallOpen.value}
        onClose={appInstallOpen.onFalse}
      />
    </>
  );

  if (searchLoading) {
    return <Loading />;
  }

  return topBarMarkup;
}
